/* Copyright (c) 2021 Paul Nebel */

import PropTypes from 'prop-types';
import React from 'react';
import Vex from 'vexflow';
import { connect } from 'react-redux';
import { isEqual } from 'lodash';

import {
    getRootNote,
    formatChordStave,
    parseChord,
} from '../core/utils/chord-parser';

class ChordStave extends React.PureComponent {
    static getDerivedStateFromProps(nextProps, prevState) {
        const { metronomeState, selectedChordSymbol } = nextProps;

        const newState = {
            ...prevState,
            metronomeState,
            selectedChordSymbol,
        };

        switch (metronomeState) {
            // case 'intro':
            // case 'pausedActive':
            case 'active':
                newState.fillStyle = '#0A0A0A';
                break;
            default:
                newState.fillStyle = '#dee5ed';
        }

        if (!isEqual(prevState, newState)) {
            return newState;
        } else {
            return null;
        }
    }

    constructor(props) {
        super(props);
        this.divRef = React.createRef();
        this.state = {
            metronomeState: props.metronomeState,
            // selectedChordSymbol: props.selectedChordSymbol,
            fillStyle: props.color || '#0A0A0A',
        };
    }

    setupChordStave() {
        try {
            const VF = Vex.Flow;

            // Create an SVG renderer and attach it to the DIV element.
            const div = this.divRef.current;
            this.renderer =
                this.renderer || new VF.Renderer(div, VF.Renderer.Backends.SVG);

            // Configure the rendering context.
            this.renderer.resize(this.props.width, this.props.height);
            const context = this.renderer.getContext();
            context.clear();
            context.setBackgroundFillStyle('#dee5ed');
            context.setStrokeStyle(this.state.fillStyle);
            context.setFillStyle(this.state.fillStyle);
            const scale = this.props.scale;
            context.scale(scale, scale);

            // Create a stave
            const stave = new VF.Stave(
                this.props.staveX,
                this.props.staveY,
                this.props.staveWidth,
                { fill_style: this.state.fillStyle }
            );

            // Add a clef and time signature.
            stave.addClef('treble');

            if (this.props.addKey) {
                stave.addKeySignature(
                    getRootNote(this.state.selectedChordSymbol) || 'C'
                );
            }

            // Connect it to the rendering context and draw!
            stave.setContext(context).draw();

            // const parsedChord = this.state.selectedChordSymbol.normalized;
            // const parsedNotes = parsedChord.notes;
            const accidentals = [];
            // adjust for c#, d# &g#....
            // switch (parsedNotes[0].toLowerCase()) {
            //     case 'c#':
            //         parsedNotes[1] = 'E#';
            //         break;
            //     case 'd#':
            //         parsedNotes[1] = 'F##';
            //         break;
            //     case 'g#':
            //         parsedNotes[1] = 'B#';
            //         break;
            //     default:
            //         break;
            // }
            const notes = formatChordStave(
                this.state.selectedChordSymbol,
                accidentals
            );
            const chord = new VF.StaveNote({
                clef: 'treble',
                keys: notes,
                duration: 'w',
            });
            for (let i = 0; i < accidentals.length; i = i + 1) {
                const currentAccidental = accidentals[i];
                if (currentAccidental !== '') {
                    chord.addAccidental(
                        i,
                        new VF.Accidental(currentAccidental)
                    );
                }
            }

            VF.Formatter.FormatAndDraw(context, stave, [chord]);
        } catch (e) {
            // write the error message to the canvas.
            console.log(e);
        }
    }

    componentDidUpdate() {
        this.setupChordStave();
    }

    // Called after render
    componentDidMount() {
        this.setupChordStave();
    }

    render() {
        return (
            <div
                ref={this.divRef}
                style={{
                    backgroundColor: 'transparent',
                    width: this.props.width,
                    height: this.props.height,
                }}
            />
        );
    }
}

const mapStateToProps = ({ metronomeState, selectedChordSymbol }) => {
    return {
        metronomeState,
        selectedChordSymbol,
    };
};

ChordStave.propTypes = {
    width: PropTypes.number,
    height: PropTypes.number,
    staveWidth: PropTypes.number,
    staveX: PropTypes.number,
    staveY: PropTypes.number,
    color: PropTypes.string,
    notes: PropTypes.arrayOf(PropTypes.string).isRequired,
    addKey: PropTypes.bool,
    metronomeState: PropTypes.string,
    selectedChordSymbol: PropTypes.object,
    scale: PropTypes.number,
};

ChordStave.defaultProps = {
    width: 300,
    height: 150,
    staveWidth: 120,
    staveX: 40,
    staveY: 20,
    color: '#555555',
    notes: ['c/4', 'e/4', 'g/4'],
    addKey: true,
    metronomeState: 'inactive',
    selectedChordSymbol: parseChord('F'),
    scale: 1,
};

export default connect(mapStateToProps, null)(ChordStave);
